import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Endpoint que devuelve tags existentes con conteo. Si lo das,
   * aparecen sugerencias autocomplete mientras el usuario tipea. */
  suggestionsUrl?: string;
};

/** Helper de normalizacion shared con el backend: trim + lowercase para
 * dedup case-insensitive. El casing original del primer tag escrito se
 * preserva al guardar. */
function normalizeTagKey(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * Input estilo "chips" para una lista de tags.
 *
 * - Enter o coma confirma el tag actual y limpia el input.
 * - Backspace en input vacio borra el ultimo chip.
 * - Click en x del chip lo quita.
 * - Si pasas `suggestionsUrl`, aparece un dropdown con tags existentes
 *   que matchean el texto. Click en sugerencia la agrega.
 * - Dedup case-insensitive: no permite agregar el mismo tag dos veces.
 */
export default function TagInput({
  value,
  onChange,
  placeholder,
  suggestionsUrl,
}: Props) {
  const [input, setInput] = useState("");
  const [allTags, setAllTags] = useState<string[]>([]);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cargar todos los tags existentes una sola vez al montar.
  useEffect(() => {
    if (!suggestionsUrl) return;
    api
      .get<{ tag: string; count: number }[]>(suggestionsUrl)
      .then((rows) => setAllTags(rows.map((r) => r.tag)))
      .catch(() => setAllTags([]));
  }, [suggestionsUrl]);

  const yaAsignados = useMemo(
    () => new Set(value.map(normalizeTagKey)),
    [value],
  );

  const sugerencias = useMemo(() => {
    const needle = normalizeTagKey(input);
    if (!needle) return [];
    return allTags
      .filter(
        (t) =>
          !yaAsignados.has(normalizeTagKey(t)) &&
          normalizeTagKey(t).includes(needle),
      )
      .slice(0, 8);
  }, [allTags, input, yaAsignados]);

  function agregar(raw: string) {
    const t = raw.trim();
    if (!t) return;
    const key = normalizeTagKey(t);
    if (yaAsignados.has(key)) {
      setInput("");
      return;
    }
    onChange([...value, t]);
    setInput("");
  }

  function quitar(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      agregar(input);
    } else if (e.key === "Backspace" && input === "" && value.length > 0) {
      e.preventDefault();
      quitar(value.length - 1);
    }
  }

  return (
    <div className="relative">
      <div
        className="input w-full flex items-center gap-1.5 flex-wrap min-h-[38px] cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            className="inline-flex items-center gap-1 max-w-full min-w-0 text-xs px-1.5 py-0.5 rounded border border-accent-500/30 bg-accent-500/10 text-accent-300"
          >
            <span className="truncate max-w-[160px] sm:max-w-[220px]">{tag}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                quitar(i);
              }}
              className="text-accent-300/70 hover:text-accent-200"
              aria-label={`Quitar ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            // Dejar tiempo a un click en sugerencia.
            setTimeout(() => setFocused(false), 120);
            if (input.trim()) agregar(input);
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[80px] sm:min-w-[120px] bg-transparent border-none outline-none text-sm"
        />
      </div>

      {focused && sugerencias.length > 0 && (
        <div className="absolute z-10 top-full left-0 right-0 mt-1 rounded border border-ink-700 bg-ink-900 shadow-lg max-h-48 overflow-y-auto">
          {sugerencias.map((t) => (
            <button
              key={t}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                agregar(t);
                inputRef.current?.focus();
              }}
              className="w-full text-left text-sm px-3 py-1.5 truncate hover:bg-ink-800 text-zinc-200 transition"
            >
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
